// Syncs layout state with the dashboard_settings row (last-write-
// wins): GET on mount (adopt if version matches), debounced PUT on
// change, writes gated until the initial read resolves. localStorage
// stays as the warm cache for first paint.

import { useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { debounce } from 'lodash'

import { fetchingAPI, apiService } from '@/api'

import { STATE_VERSION, normalizeState } from '../helpers/layout'

const DASHBOARD_TYPE = 'qa_copilot'
const SAVE_DEBOUNCE_MS = 2000

const settingsUrl = (orgId) =>
  `${apiService.scorecard}/dashboard_settings?dashboard_type=${DASHBOARD_TYPE}&requested_organization_id=${orgId}`

export const useServerLayoutSync = (orgId, state, setState) => {
  // fetchingAPI wants a dispatch for its token-expiry logout path.
  const dispatch = useDispatch()
  // Block PUTs until the initial GET has resolved (or failed -- a dead
  // server shouldn't also break local persistence).
  const readyRef = useRef(false)

  const persist = useMemo(
    () =>
      debounce((currentOrgId, currentState) => {
        // fetchingAPI hands `body` verbatim to fetch(), so it must be
        // pre-stringified -- a raw object coerces to "[object Object]"
        // and the BE 422s every save.
        fetchingAPI(
          settingsUrl(currentOrgId),
          'PUT',
          dispatch,
          JSON.stringify({ version: STATE_VERSION, ...currentState })
        ).catch(() => {
          // Server write failures are non-fatal; localStorage still has
          // the latest and the next change retries.
        })
      }, SAVE_DEBOUNCE_MS),
    [dispatch]
  )

  useEffect(() => {
    if (!orgId) {
      return undefined
    }
    let cancelled = false
    // Re-running for a NEW orgId: gate writes until this org's read
    // resolves, and drop any write still pending under the old org's
    // authz context.
    readyRef.current = false
    persist.cancel()
    fetchingAPI(settingsUrl(orgId), 'GET', dispatch)
      .then((res) => {
        if (cancelled) {
          return
        }
        const blob = res?.settings
        readyRef.current = true
        if (blob && blob.version === STATE_VERSION) {
          const normalized = normalizeState(blob)
          if (normalized) {
            setState(normalized)
          }
          return
        }
        // Missing or stale-schema blob: push the current local state
        // up NOW. (Flipping readyRef doesn't re-run the save effect,
        // so without this a first-time user's layout never uploads
        // until their next change.)
        setState((current) => {
          persist(orgId, current)
          return current
        })
      })
      .catch(() => {
        if (!cancelled) {
          readyRef.current = true
        }
      })
    return () => {
      cancelled = true
    }
    // Initial read (re-runs only on org change) -- state deps would
    // fight the debounced writes.
  }, [orgId, persist])

  useEffect(() => {
    if (!orgId || !readyRef.current) {
      return
    }
    persist(orgId, state)
  }, [orgId, state, persist])

  // Flush the pending write on unmount so a quick tab-away doesn't
  // drop the last change.
  useEffect(() => () => persist.flush(), [persist])
}
